const FIELD = 'skrepkaReloadViewV1';
type Snapshot = { href: string; values: Record<string, unknown> };
type HistoryPort = Pick<History, 'state' | 'replaceState'>;
function snapshot(value: unknown): Snapshot | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Partial<Snapshot>;
  return typeof v.href === 'string' && !!v.values && typeof v.values === 'object' && !Array.isArray(v.values) ? v as Snapshot : null;
}

/** Only a real reload restores view state. Normal navigation keeps its existing defaults. */
export function createReloadViewStore(history: HistoryPort, reload: boolean) {
  const current = () => (history.state ?? {}) as Record<string, unknown>;
  const boot = reload ? snapshot(current()[FIELD]) : null;
  const used = new Set<string>();
  const id = (owner: string, key: string) => JSON.stringify([owner, key]);
  return {
    read<T>(href: string, owner: string, key: string, valid: (value: unknown) => value is T): T | undefined {
      const slot = id(owner, key), value = boot?.values[slot];
      return boot?.href === href && !used.has(slot) && valid(value) ? value : undefined;
    },
    claim(owner: string, key: string) { used.add(id(owner, key)); },
    write(href: string, owner: string, key: string, value: unknown, remove = false) {
      try {
        const previous = snapshot(current()[FIELD]);
        const values = previous?.href === href ? { ...previous.values } : {};
        if (remove) delete values[id(owner, key)]; else values[id(owner, key)] = value;
        // Preserve React Router's key, index and user state, without creating a history entry.
        history.replaceState({ ...history.state, [FIELD]: { href, values } }, '');
      } catch { /* Restricted/oversized browser storage must not prevent navigation. */ }
    },
  };
}

export const nullableId = (value: unknown): value is number | null => value === null || typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
export const textValue = (value: unknown): value is string => typeof value === 'string';
export const nullableText = (value: unknown): value is string | null => value === null || textValue(value);
